import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { difference, sumBy } from 'lodash';
import { BehaviorSubject, Observable, Subject, map, merge, pairwise, scan, takeUntil } from 'rxjs';
import { BarcodeScannerService } from '../core/barcode-scanner/barcode-scanner.service';
import { FeedbackService } from '../core/feedback/feedback.service';
import { SCREEN_LAYOUT, ScreenLayout } from '../core/layout/layout.provider';
import { DataService } from '../data/data.service';
import { Product } from '../data/models';
import { PusherService } from '../data/pusher.service';
import { InfiniteScrollComponent } from './infinite-scroll.component';
import { ProductAction, ProductViewModel } from './product-base.component';
import { ProductDetailsService } from './product-details.service';
import { ProductVariantSelectorComponent } from './product-variant-selector.component';

interface ProductLoadingOptions {
  collectionId: number | null;
  page: number;
  filter: string;
}

interface IFilter {
  search: string;
}

function prepareProductViewModel(product: Product): ProductViewModel {
  const firstVariant = product.variants[0];
  const available = product.variants.some(v => v.available);

  return {
    ...product,
    title: firstVariant.title,
    price: firstVariant.price,
    available,
    considersStock: product.variants.every(v => v.considersStock),
    stock: sumBy(product.variants, v => v.stock || 0),
    image: firstVariant.images[0]?.url || null,
    hasMultipleVariants: product.variants.length > 1
  };
}

@Component({
    selector: 'app-products-page',
    template: `
    <div class="d-flex flex-nowrap h-100 w-100 top bottom">
      <div class="position-relative flex-shrink-0"
           [class.content]="layout === 'desktop' || screenWidth >= displayCartComponent"
           [class.w-100]="layout === 'mobile' && screenWidth < displayCartComponent"
      >
        <div *ngIf="dataService.cashBoxClosed"
             class="position-absolute top-0 left-0 bottom-0 right-0 bg-medium opacity-75 h-100 w-100 z-index-50"></div>
        <div class="d-flex flex-column h-100" [appLoadingOverlay]="loading">
          <div class="flex-grow-0 px-1 px-sm-2 px-md-3 px-lg-4 py-2" [class.pt-4]="(layout === 'mobile' && (hasFilter | async) === false)">
            <app-product-filter (filerChange)="onFilterChange($event)"></app-product-filter>
            <app-product-collection-select *ngIf="(hasFilter | async) === false"
                                           (collectionChange)="onCollectionChange($event)"></app-product-collection-select>
          </div>

          <app-infinite-scroll *ngIf="(layout === 'mobile' && screenWidth <= displayBlockViewAndroid) || (layout === 'desktop' && screenWidth <= displayBlockViewDesktop); else showBlockView"
                appScrollbarResize class="px-1 px-sm-2 px-md-3 px-lg-4 flex-grow-1 flex-shrink-1 overflow-y-auto" (scrolled)="loadNextPage()">
            <section class="d-flex flex-column justify-content-start" *ngIf="productVariants.length; else noProducts">
              <app-product-simple *ngFor="let product of productVariants; trackBy: trackByProduct" [product]="product"
                                (productAction)="onProductAction($event)"></app-product-simple>
            </section>
          </app-infinite-scroll>

          <ng-template #noProducts>
            <section class="no-products">
              <span *ngIf="!search; else noSearchedProducts">
                Dieser Kategorie sind keine Produkte zugeordnet.
              </span>
            </section>
          </ng-template>

          <ng-template #noSearchedProducts>
            Keine Produkte gefunden.
          </ng-template>

          <ng-template #showBlockView>
            <app-infinite-scroll class="px-3 px-sm-3 px-md-3 px-lg-4 pt-2 flex-grow-1 flex-shrink-1 overflow-y-auto" appScrollbarResize (scrolled)="loadNextPage()">
              <section class="flex-grow-1 flex-shrink-1 product-container" *ngIf="productVariants.length; else noProducts">
                <app-product class="product-item" *ngFor="let product of productVariants; trackBy: trackByProduct" [product]="product"
                           (productAction)="onProductAction($event)"></app-product>
              </section>
            </app-infinite-scroll>
          </ng-template>
        </div>
      </div>
      <div *ngIf="layout === 'desktop' || screenWidth >= displayCartComponent"
           class="bg-white flex-grow-1 rounded-start-6px">
        <app-cash-box-closed-page *ngIf="dataService.cashBoxClosed"></app-cash-box-closed-page>
        <app-cart-page *ngIf="!dataService.cashBoxClosed"></app-cart-page>
      </div>
    </div>
  `,
    styles: [`
    $toastSize: 240px;
    $toastMaxSize: 40vw;
    :host {
      display: block;
      height: 100%;
      width: 100%;
    }

    .content {
      width: calc(100% - 420px);
    }

    .z-index-50 {
      z-index: 50;
    }

    .product-container {
      display: grid;
      grid-template-columns: repeat(3,1fr);
      grid-column-gap: 10px;
    }

    .product-item {
      min-width: 160px;
      aspect-ratio: 1 / 1;
    }

    .no-products {
      text-align: center;
      margin-top: 50px;
    }

    @media only screen and (min-width: 710px) {
      .product-container {
        grid-template-columns: repeat(4,1fr);
      }
    }

    @media only screen and (min-width: 870px) {
      .product-container {
        grid-template-columns: repeat(5,1fr);
      }
    }

    @media only screen and (min-width: 1030px) {
      .product-container {
        grid-template-columns: repeat(6,1fr);
      }
    }

    @media only screen and (min-width: 1096px) {
      .product-container {
        grid-template-columns: repeat(3,1fr);
      }
    }

    @media only screen and (min-width: 1267px) {
      .product-container {
        grid-template-columns: repeat(4,1fr);
      }
    }

    @media only screen and (min-width: 1447px) {
      .product-container {
        grid-template-columns: repeat(5,1fr);
      }
    }

    @media only screen and (min-width: 1607px) {
      .product-container {
        grid-template-columns: repeat(6,1fr);
      }
    }
  `],
    standalone: false
})
export class ProductsPage implements OnInit, OnDestroy {
  @ViewChild(InfiniteScrollComponent, {read: ElementRef}) scrollContainer!: ElementRef;
  private _productCatalog: Record<number, ProductViewModel> = {};
  private _productOrder: number[] = [];

  get productVariants(): ProductViewModel[] {
    return this._productOrder.map(id => this._productCatalog[id]);
  }

  search = '';
  loading = false;
  pageCount = 1;

  // In android environment, if the screen width greater than this value, the products appear in block view,
  // otherwise in list view.
  displayBlockViewAndroid = 550;
  // In desktop environment, if the screen width greater than this value, the products appear in block view,
  // otherwise in list view.
  displayBlockViewDesktop = 1096;
  // In android environment, if the screen width greater than this value,
  // the cart component appears on the right side of the screen.
  displayCartComponent = 1086;

  landscape = window.matchMedia('(orientation: landscape)');
  screenWidth = window.innerWidth;

  destroy = new Subject<void>();

  private _productLoadingOptions = new BehaviorSubject<Partial<ProductLoadingOptions>>({
    collectionId: null,
    filter: '',
    page: 0
  });

  hasFilter = this._productLoadingOptions.pipe(
    map(option => !!option.filter)
  );

  constructor(@Inject(SCREEN_LAYOUT) public layout: ScreenLayout,
    public readonly modalService: NgbModal,
    private readonly productDetailsService: ProductDetailsService,
    public readonly dataService: DataService,
    private readonly pusher: PusherService,
    private readonly barcodeFeedback: FeedbackService,
    private readonly scanner: BarcodeScannerService) { }

  ngOnInit() {
    this.landscape.addEventListener('change', this.handleScreenWidthChange);
    window.addEventListener('resize', this.handleScreenWidthChange);

    merge(this.pusher.scan, this.scanner.scannerResults).pipe(takeUntil(this.destroy)).subscribe(async (scanned) => {
      const productId = this.dataService.barcodes[scanned];

      if (productId) {
        await this.dataService.addToCart(productId);
        this.barcodeFeedback.showBarcodeSuccess();
      } else {
        this.barcodeFeedback.showBarcodeError();
      }
    });

    const productLoadingOptionChanges: Observable<ProductLoadingOptions> = this._productLoadingOptions.pipe(
      scan((acc, curr) => Object.assign({}, acc, curr), {} as ProductLoadingOptions)
    );

    productLoadingOptionChanges.pipe(
      pairwise(),
      takeUntil(this.destroy)
    ).subscribe(async ([previousOption, currentOption]) => {
      // Collection id is set
      if (currentOption.collectionId) {
        // Collection changed now
        if (currentOption.collectionId !== previousOption.collectionId) {
          const products = await this.fetchCollection(currentOption.collectionId, currentOption.page);
          this.storeProducts(products);
          this.scrollContainer.nativeElement.scrollTop = 0;
          return;
        }
        // Page changed for same collection
        if (currentOption.page !== previousOption.page) {
          const products = await this.fetchCollection(currentOption.collectionId, currentOption.page);
          this.addProducts(products);
          return;
        }
        // Ignore changes if both collection id and page have not changed
        return;
      }

      // No collection id is set
      const products = await this.fetchProducts(currentOption.filter, currentOption.page);
      if (currentOption.filter !== previousOption.filter) {
        // Filters changed, reset items
        this.storeProducts(products);
        this.scrollContainer.nativeElement.scrollTop = 0;
      } else {
        // Filters unchanged, add items to list
        this.addProducts(products);
      }
    });
  }

  private storeProducts(products: ProductViewModel[]) {
    this._productCatalog = {};
    this._productOrder = [];
    this.addProducts(products);
  }

  private addProducts(products: ProductViewModel[]) {
    products.forEach((product) => this._productCatalog[product.id] = product);
    const productIds = products.map(product => product.id);
    const newIds = difference(productIds, this._productOrder);
    this._productOrder = this._productOrder.concat(newIds);
  }

  private async fetchCollection(collectionId: number, page: number): Promise<ProductViewModel[]> {
    try {
      this.loading = true;
      const paginatedCollection = await this.dataService.fetchPaginatedCollection(collectionId, {p: page});
      this.pageCount = paginatedCollection.meta.total;
      return paginatedCollection.products.map(prepareProductViewModel);
    } finally {
      this.loading = false;
    }
  }

  private async fetchProducts(search: string, page: number): Promise<ProductViewModel[]> {
    try {
      this.loading = true;
      const paginatedProducts = await this.dataService.fetchPaginatedProducts({search, p: page});
      this.pageCount = paginatedProducts.meta.total;
      return paginatedProducts.products.map(prepareProductViewModel);
    } finally {
      this.loading = false;
    }
  }

  private handleScreenWidthChange = () => {
    this.screenWidth = window.innerWidth;
  };

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    this.landscape.removeEventListener('change', this.handleScreenWidthChange);
    window.removeEventListener('resize', this.handleScreenWidthChange);
  }

  onProductAction({ id, action }: ProductAction) {
    switch (action) {
      case 'addToCart':
        this.dataService.addToCart(id);
        break;
      case 'openInfo':
        this.openProductInfo(id);
        break;
      case 'selectVariant':
        this.selectProductVariant(id);
        break;
      default:
        throw new Error(`Not implemented action: ${action} for product ${id}`);
    }
  }

  async openProductInfo(id: number) {
    await this.productDetailsService.openProductInfoModal(id);
  }

  selectProductVariant(id: number) {
    const product = this.productVariants.find(p => p.id === id);
    if (!product) {
      throw new Error(`Product (${id}) not found in fetched products.`);
    }

    const modal = this.modalService.open(ProductVariantSelectorComponent, {
      size: 'lg',
      scrollable: true
    });
    modal.componentInstance.variants = product.variants;
  }

  trackByProduct(index: number, item: ProductViewModel) {
    return item.id;
  }

  onCollectionChange(collectionId: number | null) {
    this._productLoadingOptions.next({collectionId, page: 1});
  }

  onFilterChange(filter: IFilter | null) {
    this.search = filter?.search ?? '';
    this._productLoadingOptions.next({filter: this.search, page: 1});
  }

  loadNextPage() {
    const currentPage = this._productLoadingOptions.getValue().page ?? 1;
    
    if (currentPage < this.pageCount) {
      this._productLoadingOptions.next({page: currentPage + 1});
    }
  }
}
